import { z } from 'zod';
import { cellSchema } from './cells.zod';
import { macroSchema } from './macros.zod';
import { flowCfgSchema } from './flow/flow.zod';
import { appletEventDefSchema } from './event.zod';
import { DeployParamKind, deployParamSchema } from '../../../../src/mes/deployParam.zod';
import { baseAppletVariableSchema, varOrConstSchema } from './var.zod';

const rowSchema = z.object({
    weight: z.number().default(1),
});

const columnSchema = z.object({
    weight: z.number().default(1),
});

const coordinateSchema = z.object({
    row: z.number().default(0),
    column: z.number().default(0),
});

const marginPaddingBaseSchema = z.object({
    top: z.number().default(0),
    right: z.number().default(0),
    bottom: z.number().default(0),
    left: z.number().default(0),
});

const borderSchema = z.object({
    width: z.number().default(0),
    color: z.string().default('black'),
    style: z.string().default('none'),
});

const borderStyleSchema = z.object({
    top: borderSchema,
    bottom: borderSchema,
    left: borderSchema,
    right: borderSchema,
});

export const baseGridCellSchema = z.object({
    id: z.string().uuid(),
    borderRadius: z.number(),
    border: borderStyleSchema,
    name: z.string().default(''),
    margin: marginPaddingBaseSchema,
    parentGridId: z.string().uuid(),
    elevation: z.number().default(0),
    padding: marginPaddingBaseSchema,
    coordinates: z.array(coordinateSchema),
    backgroundColor: varOrConstSchema(z.string()),
    additionalCellProperties: z.record(z.union([ z.string(), z.number(), z.boolean() ])).optional(),
});

export const regularGridCellSchema = baseGridCellSchema.extend({
    type: z.literal('regular'),
    additionalCellProperties: z.object({
        argoCellId: z.string().default(''),
    }),
});

const infoBoxGridCellSchema = baseGridCellSchema.extend({
    type: z.literal('infoBox'),
    additionalCellProperties: z.object({
        headerSourceVar: varOrConstSchema(),
        headerSourceVarTestValue: z.string().default(''),
        contentSourceVar: varOrConstSchema(),
        contentSourceVarTestValue: z.string().default(''),
        headerFontSize: z.number().default(16),
        contentFontSize: z.number().default(14),
        headerFontColor: varOrConstSchema(z.string()).default({
            val: 'black',
            type: 'const',
        }),
        contentFontColor: varOrConstSchema(z.string()).default({
            val: 'black',
            type: 'const',
        }),
        headerHorizontalAlignment: z.enum([ 'left', 'center', 'right' ]).default('left'),
        contentHorizontalAlignment: z.enum([ 'left', 'center', 'right' ]).default('left'),
        headerFontWeight: z.enum([ 'normal', 'bold', 'bolder', 'lighter' ]).default('normal'),
        headerUseItalics: z.boolean().default(false),
        contentFontWeight: z.enum([ 'normal', 'bold', 'bolder', 'lighter' ]).default('normal'),
        contentUseItalics: z.boolean().default(false),
        headerVerticalAlignment: z.enum([ 'flex-start', 'center', 'flex-end' ]).default('flex-start'),
        contentVerticalAlignment: z.enum([ 'flex-start', 'center', 'flex-end' ]).default('flex-start'),
    }),
});

const tabGroupCellSchema = baseGridCellSchema.extend({
    type: z.literal('tabGroup'),
    additionalCellProperties: z.object({
        fixedTabs: z.boolean().default(false),
        strictTabs: z.boolean().default(false),
        verticalTabs: z.boolean().default(false),
        headerVerticalAlignment: z.enum([ 'top', 'bottom' ]).default('top'),
        headerHorizontalAlignment: z.enum([ 'left', 'right' ]).default('left'),
        tabHeaderSize: z.enum([ 'hidden', 'small', 'medium', 'large', 'x-large' ]).default('medium'),
    }),
});

const baseSubGridCellSchema = baseGridCellSchema.extend({
    rows: z.array(rowSchema),
    columns: z.array(columnSchema),
});

const subGridCellSchema = baseSubGridCellSchema.extend({
    type: z.literal('subGrid'),
});

const tabInstanceCellSchema = baseSubGridCellSchema.extend({
    idx: z.number(),
    type: z.literal('tabInstance'),
    additionalCellProperties: z.object({
        label: z.string(),
    }),
});

const linkedGridCellSchema = z.discriminatedUnion('type', [ regularGridCellSchema, subGridCellSchema, tabInstanceCellSchema, tabGroupCellSchema, infoBoxGridCellSchema ]);

const gridLayoutSchema = z.object({
    enabled: z.boolean().default(true),
    linkedGridCells: z.array(linkedGridCellSchema),
    gridConfig: baseSubGridCellSchema.omit({
        coordinates: true,
        parentGridId: true,
        additionalCellProperties: true,
    }),
});

export const gridLayoutAspectRatioSchema = z.object({
    mobile: gridLayoutSchema,
    tablet: gridLayoutSchema,
    desktop: gridLayoutSchema,
    largeDisplay: gridLayoutSchema,
});

export const screenSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    order: z.number().optional(),
    activeOperator: z.object({
        /**
         * whether this screen requires an  operator.
         * @default false
         */
        required: z.boolean().default(false),
    }).nullable().default(null),
    /**
     * Cells that are used within the screen
     */
    cells: z.array(z.lazy(() => cellSchema)),
    gridLayoutAspectRatios: gridLayoutAspectRatioSchema,
});

export const runtimeScriptCfgSchema = z.object({
    id: z.string(),
    script: z.string(),
    /**
     * disables validation against the "argo lang" grammar.
     *
     * this option is "hidden" and intentionally not made visible to users
     */
    disableGrammarValidation: z.boolean().optional(),
    label: z.string().optional(),
});

const baseLocalAppletVariableSchema = baseAppletVariableSchema.extend({
    kind: z.literal('local'),
});

const localAppletVariableCfgSchema = z.discriminatedUnion('type', [
    baseLocalAppletVariableSchema.extend({
        type: z.literal('num'),
        defaultValue: z.number(),
    }),
    baseLocalAppletVariableSchema.extend({
        type: z.literal('str'),
        defaultValue: z.string(),
    }),
    baseLocalAppletVariableSchema.extend({
        type: z.literal('bool'),
        defaultValue: z.boolean(),
    }),
    baseLocalAppletVariableSchema.extend({
        type: z.literal('empty'),
        defaultValue: z.null(),
    }),
]);

const baseDeployParamAppletVariableSchema = baseAppletVariableSchema.extend({
    kind: z.literal('deployParam'),
    deployParamKind: z.literal(DeployParamKind.localVarValue),
});

const deployParamAppletVariableCfgSchema = z.union([
    deployParamSchema(baseDeployParamAppletVariableSchema.extend({
        type: z.literal('str'),
        defaultValue: z.string(),
    })),
    deployParamSchema(baseDeployParamAppletVariableSchema.extend({
        type: z.literal('bool'),
        defaultValue: z.boolean(),
    })),
    deployParamSchema(baseDeployParamAppletVariableSchema.extend({
        type: z.literal('num'),
        defaultValue: z.number(),
    })),
    deployParamSchema(baseDeployParamAppletVariableSchema.extend({
        type: z.literal('empty'),
        defaultValue: z.null(),
    })),
]);

export const appletVariableCfgSchema = z.record(z.union([ localAppletVariableCfgSchema, deployParamAppletVariableCfgSchema ])).default({});

export const appletDesignVersionConfigSchema_v4 = z.object({
    /** holds the name of a variable. if set, the config should only be allowed to change when this variable's value changes, or lacks a value entirely */
    lockout: z.string().nullable(),
    screens: z.array(screenSchema),
    /**
     * if null, an operator in general is not required for this config anywhere
     */
    activeOperator: z.object({
        /**
         * the provider (from the auth service) that operators should use to authenticate
         * @default 'badge'
         */
        type: z.literal('badge'),
        /**
         * if null, the operator login will not expire
         * @default null
         */
        expiration: z.object({
            timeoutMinutes: z.number(),
            /**
             * whether re-authentication should only be required after inactivity of `timeoutMinutes`,
             * or just since the last auth
             *
             * @default true
             */
            inactivityOnly: z.boolean().default(true),
        }).nullable().default(null),
    }).nullable().default(null),
    macros: z.array(macroSchema),
    /**
     * The config schema version, should be changed every time there's breaking change in the config typedefs
     */
    schemaVersion: z.literal(4),
    scripts: z.array(runtimeScriptCfgSchema).default([]),
    fontFamily: z.union([
        z.literal('serif'),
        z.literal('sans-serif'),
        z.literal('monospace'),
        z.literal('cursive'),
        z.literal('fantasy'),
        z.literal('system-ui'),
    ]).default('monospace'),
    flows: z.record(flowCfgSchema).default({}),
    /**
     * All the user defined applet events,
     * mapping event IDs to their user defined names.
     *
     * Other implied (non user-defined) events will be possible, such as a button cell press event.
     */
    userAppletEvents: z.record(appletEventDefSchema),
    /** keyed by applet variable id */
    appletVariables: appletVariableCfgSchema,
});

export type AppletDesignVersionConfig_V4 = z.infer<typeof appletDesignVersionConfigSchema_v4>;
